import React from "react";
import { useTranslation } from "utilities/i18n";
import { useFactoryResetMutation } from "generated/graphql";
import { DISMISS, useNotification } from "collections/Notifications";
import { DefaultModalForm, useModalDispatch } from "collections/Modal";
import { SubmitButton } from "elements";
import { useDeviceCallState } from "./hooks";

export const FactoryResetDeviceModal: React.FC<{
  id: string;
  onReset: () => void;
}> = ({ id, onReset }) => {
  const { t } = useTranslation();
  const [, reset] = useFactoryResetMutation();
  const [addNotification] = useNotification();
  const dispatchEvent = useModalDispatch();
  const { inCall } = useDeviceCallState(id);

  const factoryResetDevice = async () => {
    const { data } = await reset({ deviceId: id });
    const response = data?.factoryReset;

    if (response?.success) {
      addNotification({
        type: "success",
        message: t("FACTORY_RESET_DEVICE_SUCCESS"),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
      onReset();
      dispatchEvent();
    } else {
      addNotification({
        type: "error",
        message: t("FACTORY_RESET_DEVICE_FAILURE", {
          errorMessage: response?.error
        }),
        persistThroughNav: true,
        dismiss: DISMISS.USER_ONLY
      });
      dispatchEvent();
    }
  };

  return (
    <DefaultModalForm
      title={t("FACTORY_RESET_DEVICE")}
      onSubmit={async () => {
        await factoryResetDevice();
      }}
      name="RestartDevice"
    >
      {({ isSubmitting }) => (
        <>
          {inCall && (
            <p className="warning">
              {t("FACTORY_RESET_DEVICE_CONFIRMATION_IN_CALL")}
            </p>
          )}
          {t("FACTORY_RESET_DEVICE_CONFIRMATION")}
          <div className="btn-container">
            <button
              type="button"
              className="btn"
              data-testid="cancel"
              onClick={dispatchEvent}
            >
              {t("CANCEL")}
            </button>
            <SubmitButton
              isSubmitting={isSubmitting}
              testId="reset"
              labelKey="FACTORY_RESET_DEVICE"
              submittingLabelKey="FACTORY_RESET_DEVICE_IN_PROGRESS"
            />
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};
